/**
 * 通知（worker が書いた督促・担当未定・未受講）。
 *
 * SES 未設定の間はこれが督促の唯一の到達経路なので、未読件数は縦メニューと下部タブに常時出す
 * （ログイン中は 60 秒ごとに取り直す）。一覧の画面（#/notifications）も同じ store を読む。
 */
import { useSyncExternalStore } from "react";
import { api } from "./api.js";

export interface Notice {
  id: string; kind: string; title: string; body: string;
  linkPath: string | null; readAt: string | null; createdAt: string;
  /** M-19: 文のキーと値（古い行は null。そのときは title/body を出す）。 */
  messageKey?: string | null; params?: Record<string, unknown> | null;
}

interface State { items: Notice[] | null; unread: number; error: unknown }
let state: State = { items: null, unread: 0, error: null };
const listeners = new Set<() => void>();
function set(next: Partial<State>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

let timer: ReturnType<typeof setInterval> | null = null;

export const notifications = {
  load(): Promise<void> {
    return api.get<{ notifications: Notice[]; unread: number }>("/notifications?limit=30")
      .then((r) => set({ items: r.notifications, unread: r.unread, error: null }))
      .catch((error: unknown) => set({ error }));
  },
  start(): void {
    if (timer) return;
    void notifications.load();
    timer = setInterval(() => { if (document.visibilityState === "visible") void notifications.load(); }, 60_000);
  },
  stop(): void {
    if (timer) clearInterval(timer);
    timer = null;
    state = { items: null, unread: 0, error: null };
  },
  /** 既読にする（成功後の値で置き換える）。 */
  async markRead(ids?: string[]): Promise<void> {
    const r = await api.post<{ unread: number }>("/notifications/read", ids ? { ids } : {});
    const now = new Date().toISOString();
    set({
      unread: r.unread,
      items: (state.items ?? []).map((x) => (!ids || ids.includes(x.id)) && !x.readAt ? { ...x, readAt: now } : x),
    });
  },
};

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function useNotifications(): State {
  return useSyncExternalStore(subscribe, () => state);
}
